/*
 * MemoryEditor — Pure-Wrapper um eine ProseEditor-Instanz (Spec §3, §5, §9).
 *
 * Phase 4: open/close-Lifecycle, ProseEditor-Instantiierung, Memory-Lock,
 * Memory-Roundtrip, Render mit Empty-State-Hint, Diktat-Verdrahtung.
 *
 * Pure-Wrapper-Strategie (Spec §5):
 *   - KEINE Aenderung an EditorModel/EditorInput/EditorView
 *   - alle Eingrenzungen leben im Wrapper, vor der Delegation
 *
 * Pane-Integration:
 *   - PaneId.MEMORY ist standardmaessig HIDDEN
 *   - open() schaltet auf FULLSCREEN → andere Panes werden ausgeblendet
 *   - close() schaltet auf HIDDEN → calculateBounds stellt das vorherige
 *     Layout wieder her
 *
 * Hinweis Spec-Drift (2026-05-06): Die Spec §5 nennt "Cmd+Shift+L" als
 * Section-Lock-Toggle. Der Code in EditorInput.js:699 nutzt aber Alt+Enter
 * — wir filtern entsprechend.
 */

import { MemoryPersistence } from './MemoryPersistence.js'
import {
    memoryStringToParagraphs, paragraphsToMemoryString
} from './memoryRoundtrip.js'
import { PaneId, Style } from '../config.js'
import { renderEmptyHint } from '../editor/emptyHint.js'
import {
    attachProseEditorToPane, detachProseEditorFromPane,
    syncProseEditorBounds, isProseEditorModelEmpty,
    applyCommonPaneKeyFilters, showPaneEditorToast
} from '../editor/paneEditorLifecycle.js'

/**
 * Empty-State-Hint (Spec §2 / §9). Wird gerendert, wenn das Memory leer ist.
 */
export const MEMORY_EMPTY_HINT =
    'Your AI memory is empty. Anything you write here will be visible to the AI '
    + 'in every conversation. Use it to record your preferences, your role, '
    + 'ongoing projects, or anything else you want the AI to remember.'

const TOAST_TWO_TAB =
    'Memory is being edited in another tab. Please close that tab first.'

export class MemoryEditor {
    /**
     * @param {object} opts
     * @param {object} opts.layout - LayoutManager
     * @param {object} [opts.dialogManager]
     * @param {object} [opts.renderer] - Renderer-Referenz (proportional)
     * @param {Function} [opts.persistenceFactory] - Test-Override fuer
     *   MemoryPersistence-Instanz; default = new MemoryPersistence()
     * @param {Function} [opts.onClose] - Callback nach close() (z.B. Fokus
     *   zurueck auf Main)
     */
    constructor(opts = {}) {
        this.layout = opts.layout || null
        this.dialogManager = opts.dialogManager || null
        this._renderer = opts.renderer || null
        this._coreConsole = opts.coreConsole || null
        this._persistenceFactory = opts.persistenceFactory
            || ((o) => new MemoryPersistence(o))
        this._onClose = typeof opts.onClose === 'function' ? opts.onClose : null
        // Spec §10 Phase 3: Memory-Editor wird genauso gepruefft wie Main —
        // der Bootstrap reicht den globalen SpellCheckManager rein. Der
        // Manager kennt die Memory-Paragraphen ueber den `getParagraph`-
        // Callback in main.js, der editor.model UND memoryEditor.proseEditor
        // .model durchsucht (UUID-IDs kollidieren nicht).
        this._spellCheckManager = opts.spellCheckManager || null
        this._spellCheckUnsubscribe = null

        // Test-Override: opts.proseEditor erlaubt es, einen Mock-ProseEditor
        // direkt zu injizieren (Phase-3-Tests fuer isModelEmpty etc.). In der
        // Produktion wird er erst in open() instanziiert.
        this.proseEditor = opts.proseEditor || null
        this.persistence = null
        this.isOpen = false
        // Vor open() aktiver Editor-Pane — beim close() wird er wiederhergestellt
        this._previousActivePane = null

        // 'idle' | 'loading' | 'ready' | 'error'
        this._loadingStatus = 'idle'
        // Letzter Server-Stand wird ausschliesslich in MemoryPersistence
        // getrackt — der Editor selbst behaelt keinen redundanten Cache, sonst
        // koennen die beiden auseinanderlaufen (z.B. wenn ein PUT fehlschlaegt
        // und der Editor schon "gesaved" denkt, aber der Server stale ist).
    }

    isActive() { return this.isOpen }

    /**
     * Oeffnet den Memory-Editor: Lock acquiren, Memory laden, ProseEditor
     * instanziieren, Pane auf FULLSCREEN.
     *
     * @returns {Promise<boolean>} true bei Erfolg, false bei Two-Tab-Konflikt
     */
    async open() {
        if (this.isOpen) return true
        if (!this.layout || !this.layout.panes[PaneId.MEMORY]) {
            throw new Error('MemoryEditor.open() requires layout with PaneId.MEMORY')
        }

        // Persistence + Lock acquire VOR Pane-Switch — wenn der Lock blockt,
        // soll der User keine Layout-Aenderung sehen.
        this.persistence = this._persistenceFactory({
            onLockLost: () => this._handleLockLost(),
            onSaveError: (err) => this._handleSaveError(err)
        })
        try {
            await this.persistence.lock()
        } catch (err) {
            this.persistence = null
            if (err && err.code === 'memory_locked') {
                this._showToast(TOAST_TWO_TAB)
                // Auch in der Core-Console loggen — Boris will den seltenen
                // Fall sichtbar haben, falls er die Toast verpasst.
                if (this._coreConsole && typeof this._coreConsole.logError === 'function') {
                    try { this._coreConsole.logError(TOAST_TWO_TAB) } catch { /* ignore */ }
                }
                return false
            }
            // Andere Fehler (Netzwerk, Auth, ...) bubblen — Caller entscheidet
            throw err
        }

        // Pane sichtbar machen + ProseEditor instantiieren via geteiltem Helper
        const attached = attachProseEditorToPane({
            layout: this.layout,
            paneId: PaneId.MEMORY,
            renderer: this._renderer
        })
        this._previousActivePane = attached.previousActivePane
        this.proseEditor = attached.proseEditor

        // Memory laden — UI ist schon offen mit "Loading…"-State
        this._loadingStatus = 'loading'
        let memoryString = ''
        try {
            const data = await this.persistence.load()
            memoryString = data.memory || ''
            this._loadingStatus = 'ready'
        } catch (err) {
            this._loadingStatus = 'error'
            console.warn('Memory load failed:', err.message)
        }
        // persistence.load() hat den Server-Stand schon intern als
        // _lastSavedText gemerkt — kein Doppel-Cache hier.

        // String → Paragraphen → Modell
        const paragraphs = memoryStringToParagraphs(memoryString)
        this.proseEditor.applyRemoteChanges(() => {
            this.proseEditor.model.setParagraphsRT(paragraphs)
        })

        // Spellcheck verkabeln: View bekommt den globalen Manager, Model
        // pflegt einen eigenen Dirty-Listener. Memory hat nur das text-Feld
        // — note/info sind im Editor-Layout dort gar nicht sichtbar.
        if (this._spellCheckManager) {
            this.proseEditor.view.spellCheckManager = this._spellCheckManager
            this._spellCheckUnsubscribe = this.proseEditor.model.addParagraphDirtyListener(
                (paraId) => {
                    this._spellCheckManager.markDirty(paraId, 'text')
                    if (this.proseEditor && this.proseEditor.view) {
                        const cursor = this.proseEditor.model.cursor
                        if (cursor) {
                            this.proseEditor.view._lastEditedCursor = {
                                paragraphId: cursor.paragraphId,
                                field:       cursor.field || 'text',
                                offset:      cursor.offset
                            }
                        }
                    }
                }
            )
            // Initial-Sweep — sonst tauchen Wellen erst nach der ersten
            // Mutation auf, was bei Memory-Paragraphen nervt (User scrollt
            // durch eine bestehende Liste).
            for (const p of this.proseEditor.model.paragraphs) {
                this._spellCheckManager.checkNow(p.id, 'text')
            }
        }

        this.isOpen = true
        return true
    }

    /**
     * Schliesst den Memory-Editor: pending Save flushen, Lock freigeben,
     * Pane wieder verstecken.
     *
     * @param {object} [opts]
     * @param {boolean} [opts.flush=true] - false ueberspringt den finalen Save
     *   (z.B. nach onLockLost — Save wuerde eh 409 kriegen)
     */
    async close(opts = {}) {
        if (!this.isOpen) return
        const flush = opts.flush !== false

        // Pending-Save flushen, dann Lock freigeben
        if (this.persistence) {
            if (flush) {
                try { await this.persistence.flushNow() } catch (err) {
                    console.warn('Memory flush failed on close:', err.message)
                }
            }
            try { await this.persistence.unlock() } catch (err) {
                console.warn('Memory unlock failed on close:', err.message)
            }
            this.persistence = null
        }

        // Pane verstecken + vorherigen Editor-Pane wiederherstellen via Helper
        detachProseEditorFromPane({
            layout: this.layout,
            paneId: PaneId.MEMORY,
            previousActivePane: this._previousActivePane
        })
        this._previousActivePane = null

        // Spellcheck-Listener abkoppeln, bevor der ProseEditor weg ist —
        // sonst sieht der naechste Memory-Open noch alte Listener-Closures.
        if (this._spellCheckUnsubscribe) {
            try { this._spellCheckUnsubscribe() } catch { /* ignore */ }
            this._spellCheckUnsubscribe = null
        }

        this.proseEditor = null
        this.isOpen = false
        this._loadingStatus = 'idle'

        if (this._onClose) {
            try { this._onClose() } catch (err) {
                console.warn('MemoryEditor.onClose threw:', err.message)
            }
        }
    }

    // ─── Keydown-Filter (Phase 3) ──────────────────────────────────────────

    /**
     * Filtert problematische Eingaben VOR der Delegation an ProseEditor.
     *   - true  → Wrapper hat den Event behandelt (preventDefault), Caller
     *             soll ihn NICHT mehr an proseEditor.input.handleKeydown
     *             weiterreichen.
     *   - false → Wrapper laesst durch; Caller delegiert an proseEditor.
     *
     * @param {KeyboardEvent} e
     * @returns {boolean}
     */
    handleKeydown(e) {
        // Memory hat keinen Bedarf an Filtern jenseits der gemeinsamen
        // Set (Separator, Suche, Cmd+Enter, Alt+Enter). Block-Format-
        // Shortcuts (Cmd+B/I/U) sollen DURCHGEHEN — Memory unterstuetzt
        // formatierten Text.
        return applyCommonPaneKeyFilters(e)
    }

    // ─── Render ────────────────────────────────────────────────────────────

    /**
     * Wird vom Render-Loop in main.js gerufen, wenn der Memory-Pane sichtbar
     * ist. Delegiert an ProseEditor.render() und blendet bei leerem Modell
     * den Empty-State-Hint darueber.
     */
    render(offsetX, offsetY, setCharFn) {
        if (!this.proseEditor) return

        // Loading: Hint statt Modell rendern
        if (this._loadingStatus === 'loading') {
            this._renderHintLine(offsetX, offsetY, 'Loading…', setCharFn,
                this.proseEditor.view.width || 80)
            return
        }

        this.proseEditor.render(offsetX, offsetY, setCharFn)

        if (this.isModelEmpty()) {
            this._renderEmptyHint(offsetX, offsetY, setCharFn)
        }
    }

    /**
     * Gibt die Cursor-Position relativ zum Pane zurueck. Wird vom main.js-
     * Sync-Pfad genutzt, um Pane.cursorPosition zu aktualisieren.
     */
    getPaneCursorPosition() {
        return this.proseEditor ? this.proseEditor.getPaneCursorPosition() : null
    }

    /**
     * Wird vom main.js-Render-Loop nach Resize gerufen — die Pane-Bounds
     * koennen sich aendern, wenn das Browser-Fenster gezogen wird.
     */
    syncBounds() {
        syncProseEditorBounds({
            proseEditor: this.proseEditor,
            layout: this.layout,
            paneId: PaneId.MEMORY
        })
    }

    /**
     * Schreibt den aktuellen Modell-Stand (als Markdown-String) in den
     * Persistence-Debouncer. Wird vom main.js-Render-Loop gerufen, wenn der
     * Memory-ProseEditor dirty ist.
     */
    scheduleSave() {
        if (!this.proseEditor || !this.persistence) return
        const text = paragraphsToMemoryString(this._modelParagraphs())
        // Persistence filtert "Server hat das schon" intern — kein Doppel-Cache
        // hier (sonst denkt der Editor "gesaved" auch wenn das PUT fehlschlug).
        this.persistence.scheduleSave(text)
    }

    // ─── Empty-State-Helpers ───────────────────────────────────────────────

    /**
     * True wenn das Modell genau einen Paragraphen mit leerem Text hat.
     * Geteilte Helper-Implementierung — siehe `paneEditorLifecycle.js`.
     */
    isModelEmpty() {
        return isProseEditorModelEmpty(this.proseEditor)
    }

    /** @returns {string} */
    getEmptyHint() {
        return MEMORY_EMPTY_HINT
    }

    // ─── Interne Helfer ────────────────────────────────────────────────────

    _modelParagraphs() {
        // Serialisierte Paragraph-Daten — kompatibel mit paragraphsToMemoryString
        const model = this.proseEditor?.model
        if (!model || !Array.isArray(model.paragraphs)) return []
        return model.paragraphs.map(p => ({
            text: p.text || '',
            formats: p.formats,
            links: p.links,
            headingLevel: p.headingLevel || 0,
            listType: p.listType || null,
            listLevel: p.listLevel || 0,
            blockquote: !!p.blockquote
        }))
    }

    _renderEmptyHint(offsetX, offsetY, setCharFn) {
        const view = this.proseEditor?.view
        const width = view?.width || 80
        renderEmptyHint(MEMORY_EMPTY_HINT, offsetX, offsetY, setCharFn, width, this._renderer)
    }

    _renderHintLine(x, y, text, setCharFn, maxWidth) {
        // Loading-State nutzt den Mono-Pfad direkt — Proportional-Branch des
        // gemeinsamen Helpers braucht den vollen Renderer, hier reicht setChar.
        const len = Math.min(text.length, maxWidth)
        for (let i = 0; i < len; i++) {
            setCharFn(x + i, y, text[i], Style.EDITOR_PLACEHOLDER)
        }
    }

    _handleLockLost() {
        // Lock weg → Editor schliessen ohne weiteren Save (waere ohnehin 409)
        this._showToast(TOAST_TWO_TAB)
        // close() asynchron, aber fire-and-forget OK fuer den Lock-Lost-Pfad
        this.close({ flush: false }).catch(err =>
            console.warn('close() after lock lost failed:', err.message)
        )
    }

    _handleSaveError(err) {
        if (err && err.code === 'memory_too_large') {
            this._showToast(`Memory full (${err.maxBytes || 51200} byte limit)`)
        } else if (err && err.code !== 'memory_locked') {
            console.warn('Memory save failed:', err.message)
        }
    }

    _showToast(message) {
        showPaneEditorToast(this.dialogManager, 'MemoryEditor', message)
    }
}
